import { useMemo, useState, useCallback } from "react";
import { normalizarTexto } from "../utils/formato";

/**
 * Buscador + orden + paginación, en cliente, para las 5 bandejas.
 *
 * Por qué en cliente y no en el backend (decidido con Liber, ver
 * docs/Decisions.md): la API ya devuelve todas las filas en una sola llamada
 * (`GET /review-items` no tiene límite ni offset), así que paginar acá no
 * reduce el payload — resuelve la lectura y la navegación, que es el problema
 * real hoy. Server-side (OFFSET/FETCH) queda para cuando el volumen llegue a
 * miles de filas.
 *
 * Detalle importante del orden: el backend NO tiene ORDER BY, así que SQL
 * Server puede devolver las filas en cualquier orden entre una consulta y
 * otra. Sin paginación eso pasaba desapercibido; con paginación se vuelve
 * visible (una fila podría "saltar" de página al recargar). El orden por
 * defecto de acá lo estabiliza sin tocar el backend.
 */

export const FILAS_POR_PAGINA = 50;

export type DireccionOrden = "asc" | "desc";

export interface OrdenTabla<T> {
  /** Clave de la columna por la que se ordena. */
  columna: keyof T & string;
  direccion: DireccionOrden;
}

interface Opciones<T> {
  /** Campos por los que filtra el buscador (ej. proveedor, RUT, nro). */
  camposBusqueda: Array<keyof T>;
  /** Orden inicial. Conviene que sea estable y significativo por bandeja. */
  ordenInicial: OrdenTabla<T>;
  /** Campos que deben compararse como número, no como texto (montos). */
  camposNumericos?: Array<keyof T>;
  /**
   * Campo único por fila (en la práctica `id`), usado SOLO para desempatar.
   *
   * No es decorativo: sin esto el orden no es determinista cuando varias
   * filas empatan en la columna ordenada — y empatan seguido (docenas de
   * facturas del mismo día, o varias por el mismo monto). `Array.sort` es
   * estable, así que ante un empate conserva el orden de ENTRADA... que es
   * justo lo que el backend no garantiza (no tiene `ORDER BY`). Resultado:
   * una fila podía cambiar de página entre dos recargas. Detectado por un
   * test de esta misma ronda, que expuso que la afirmación original
   * ("el orden por defecto lo estabiliza") era incompleta.
   */
  claveEstable: keyof T;
}

export interface TablaBandeja<T> {
  /** Filas de la página actual — es lo que hay que renderizar. */
  filas: T[];
  /** Todas las filas que pasan el buscador (sin paginar) — para los totales. */
  filtradas: T[];
  busqueda: string;
  setBusqueda: (valor: string) => void;
  orden: OrdenTabla<T>;
  /** Click en un encabezado: alterna asc/desc si ya es la columna activa. */
  alternarOrden: (columna: keyof T & string) => void;
  pagina: number;
  totalPaginas: number;
  irAPagina: (pagina: number) => void;
  /** Índices 1-based para el texto "Mostrando X-Y de Z". */
  desde: number;
  hasta: number;
  total: number;
}

/**
 * T va sin constraint a propósito: `T extends Record<string, unknown>` no
 * acepta interfaces declaradas (ReviewItem no tiene index signature) y, peor,
 * degradaba el tipo de las filas devueltas a Record<string, unknown>,
 * perdiendo el tipado de cada campo en las páginas.
 */
export function useTablaBandeja<T>(
  items: T[],
  { camposBusqueda, ordenInicial, camposNumericos = [], claveEstable }: Opciones<T>
): TablaBandeja<T> {
  const [busqueda, setBusquedaRaw] = useState("");
  const [orden, setOrden] = useState<OrdenTabla<T>>(ordenInicial);
  const [pagina, setPagina] = useState(1);

  // Buscar u ordenar vuelve a la página 1: si estabas en la página 4 y el
  // resultado filtrado tiene 1 página, quedarías mirando una página vacía.
  const setBusqueda = useCallback((valor: string) => {
    setBusquedaRaw(valor);
    setPagina(1);
  }, []);

  const alternarOrden = useCallback((columna: keyof T & string) => {
    setOrden((prev) =>
      prev.columna === columna
        ? { columna, direccion: prev.direccion === "asc" ? "desc" : "asc" }
        : { columna, direccion: "asc" }
    );
    setPagina(1);
  }, []);

  const filtradas = useMemo(() => {
    const termino = normalizarTexto(busqueda.trim());
    const base = !termino
      ? items
      : items.filter((item) =>
          camposBusqueda.some((campo) => {
            const valor = item[campo];
            if (valor === null || valor === undefined) return false;
            return normalizarTexto(String(valor)).includes(termino);
          })
        );

    const esNumerico = camposNumericos.includes(orden.columna as keyof T);
    const factor = orden.direccion === "asc" ? 1 : -1;

    // Copia antes de ordenar — nunca mutar el array que viene por props.
    return [...base].sort((a, b) => {
      const va = a[orden.columna];
      const vb = b[orden.columna];

      // Los nulos van siempre al final, sin importar la dirección: una fila
      // sin monto o sin fecha no debería encabezar la tabla. Ojo: se compara
      // el par (no se aplica `factor`) para que el criterio no se invierta.
      const aVacio = va === null || va === undefined;
      const bVacio = vb === null || vb === undefined;
      if (aVacio && bVacio) return desempatar(a, b);
      if (aVacio) return 1;
      if (bVacio) return -1;

      const comparacion = esNumerico
        ? Number(va) - Number(vb)
        : String(va).localeCompare(String(vb), "es-UY", { numeric: true });

      // Empate en la columna ordenada -> desempatar por la clave única, para
      // que el orden sea total y no dependa del orden de entrada (ver
      // `claveEstable` arriba).
      if (comparacion === 0) return desempatar(a, b);
      return comparacion * factor;
    });

    function desempatar(a: T, b: T): number {
      return String(a[claveEstable]).localeCompare(String(b[claveEstable]));
    }
  }, [items, busqueda, camposBusqueda, orden, camposNumericos, claveEstable]);

  const total = filtradas.length;
  const totalPaginas = Math.max(1, Math.ceil(total / FILAS_POR_PAGINA));
  // Si el filtro dejó menos páginas que la actual, se cae a la última válida.
  const paginaValida = Math.min(pagina, totalPaginas);
  const inicio = (paginaValida - 1) * FILAS_POR_PAGINA;

  const filas = useMemo(
    () => filtradas.slice(inicio, inicio + FILAS_POR_PAGINA),
    [filtradas, inicio]
  );

  const irAPagina = useCallback(
    (destino: number) => setPagina(Math.max(1, destino)),
    []
  );

  return {
    filas,
    filtradas,
    busqueda,
    setBusqueda,
    orden,
    alternarOrden,
    pagina: paginaValida,
    totalPaginas,
    irAPagina,
    desde: total === 0 ? 0 : inicio + 1,
    hasta: Math.min(inicio + FILAS_POR_PAGINA, total),
    total,
  };
}
